// Lightweight REST client for personal-data API.
// Sends credentials: 'include' so Better Auth cookies are attached cross-origin.

const API_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ||
  'http://localhost:8787';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    let code = 'http_error';
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      if (body.error) code = body.error;
      if (body.message) message = body.message;
    } catch {
      // body not JSON — keep defaults
    }
    throw new ApiError(res.status, code, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ─── /api/me ───────────────────────────────────────────────────────────────
export type MePreferences = {
  language: string;
  defaultLayer: string;
  defaultProfileId: string | null;
  theme: 'light' | 'dark' | 'auto';
};

export type MeResponse = {
  user: { id: string; email: string; name: string | null; image: string | null };
  preferences: MePreferences | null;
};

export const me = {
  get: () => request<MeResponse>('/api/me'),
  patch: (body: { name?: string; preferences?: Partial<MePreferences> }) =>
    request<{ ok: true }>('/api/me', { method: 'PATCH', body: JSON.stringify(body) }),
  delete: () => request<{ ok: true }>('/api/me', { method: 'DELETE' }),
  // F3.6 GDPR data portability — returns user payload as JSON for download.
  exportData: () => request<Record<string, unknown>>('/api/me/export'),
};

// ─── /api/profiles/me/quiz ─────────────────────────────────────────────────
// Persisted quiz state — survives the anon → registered transition so the
// user who completed the quiz before signing in lands on their /quiz/result
// instead of a blank /quiz on the next visit.

export type QuizStateResponse = {
  answers: unknown;
  result: unknown | null;
  startedAt: string | null;
  completedAt: string | null;
  updatedAt: string;
};

export type QuizUpsertInput = {
  answers: unknown;
  result?: unknown | null;
  startedAt?: string | null;
};

export const quizState = {
  /** Returns null if no saved quiz on file (instead of throwing 404). */
  get: async (): Promise<QuizStateResponse | null> => {
    try {
      return await request<QuizStateResponse>('/api/profiles/me/quiz');
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
  upsert: (body: QuizUpsertInput) =>
    request<{ ok: true; completedAt: string | null }>('/api/profiles/me/quiz', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  clear: () => request<{ ok: true }>('/api/profiles/me/quiz', { method: 'DELETE' }),
};

// ─── /api/shortlists ───────────────────────────────────────────────────────
export type Shortlist = {
  id: string;
  name: string;
  countryCode: string;
  comuneIstats: string[];
  notes: Record<string, string> | null;
  createdAt: number;
  updatedAt: number;
};

export type CreateShortlistInput = {
  name: string;
  countryCode?: string;
  comuneIstats: string[];
  notes?: Record<string, string>;
};

export type UpdateShortlistInput = {
  name?: string;
  countryCode?: string;
  comuneIstats?: string[];
  notes?: Record<string, string> | null;
};

export const shortlists = {
  list: () => request<{ items: Shortlist[] }>('/api/shortlists').then((r) => r.items),
  get: (id: string) => request<Shortlist>(`/api/shortlists/${id}`),
  create: (body: CreateShortlistInput) =>
    request<{ id: string }>('/api/shortlists', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: UpdateShortlistInput) =>
    request<{ ok: true }>(`/api/shortlists/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id: string) =>
    request<{ ok: true }>(`/api/shortlists/${id}`, { method: 'DELETE' }),
};

// ─── /api/profiles ─────────────────────────────────────────────────────────
export type ProfileWeights = Record<string, number>;
export type ScoringProfile = {
  id: string;
  name: string;
  description: string | null;
  weights: ProfileWeights;
  filters: unknown;
  isPublic: boolean;
  createdAt: number;
  updatedAt: number;
};

export const profiles = {
  list: () => request<{ items: ScoringProfile[] }>('/api/profiles').then((r) => r.items),
  get: (id: string) => request<ScoringProfile>(`/api/profiles/${id}`),
  create: (body: Omit<ScoringProfile, 'id' | 'createdAt' | 'updatedAt' | 'isPublic'> & { isPublic?: boolean }) =>
    request<{ id: string }>('/api/profiles', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Partial<ScoringProfile>) =>
    request<{ ok: true }>(`/api/profiles/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id: string) =>
    request<{ ok: true }>(`/api/profiles/${id}`, { method: 'DELETE' }),
  listPublic: (limit = 20) =>
    request<{ items: Omit<ScoringProfile, 'isPublic' | 'updatedAt' | 'filters'>[] }>(
      `/api/public/profiles?limit=${limit}`,
    ).then((r) => r.items),
};

// ─── /api/pins ─────────────────────────────────────────────────────────────
export type Pin = {
  id: string;
  type: 'idealista' | 'custom';
  title: string;
  url: string | null;
  lat: number;
  lon: number;
  countryCode: string;
  comuneIstat: string | null;
  metadata: unknown;
  notes: string | null;
  createdAt: number;
};

export const pins = {
  list: () => request<{ items: Pin[] }>('/api/pins').then((r) => r.items),
  create: (body: Omit<Pin, 'id' | 'createdAt'>) =>
    request<{ id: string }>('/api/pins', { method: 'POST', body: JSON.stringify(body) }),
  delete: (id: string) =>
    request<{ ok: true }>(`/api/pins/${id}`, { method: 'DELETE' }),
};

// ─── /api/extract-url ──────────────────────────────────────────────────────
export type ExtractUrlResult = {
  title: string | null;
  address: string | null;
  lat: number | null;
  lon: number | null;
  price: string | null;
  description: string | null;
  error?: string;
};

/** Fetch a property listing URL server-side and extract structured metadata. */
export function extractUrl(url: string): Promise<ExtractUrlResult> {
  return request<ExtractUrlResult>('/api/extract-url', {
    method: 'POST',
    body: JSON.stringify({ url }),
    credentials: 'omit', // non-credentialed endpoint
  });
}

// ─── /api/annotations (F5.7) ───────────────────────────────────────────────
export type Annotation = {
  id: string;
  type: 'note' | 'area' | 'line';
  lat: number;
  lon: number;
  text: string;
  color: string | null;
  geometry: string | null;
  createdAt: number;
};

export const annotations = {
  list: () => request<{ items: Annotation[] }>('/api/annotations').then((r) => r.items),
  create: (body: Omit<Annotation, 'id' | 'createdAt'>) =>
    request<{ id: string }>('/api/annotations', { method: 'POST', body: JSON.stringify(body) }),
  delete: (id: string) =>
    request<{ ok: true }>(`/api/annotations/${id}`, { method: 'DELETE' }),
};

// ─── /api/feedback (F6.5) ──────────────────────────────────────────────────
export type FeedbackCategory = 'bug' | 'idea' | 'data' | 'other';

export const feedback = {
  submit: (body: {
    category: FeedbackCategory;
    message: string;
    email?: string;
    pageUrl?: string;
  }) =>
    request<{ id: string; ok: true }>('/api/feedback', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};
